import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageHero from "../components/PageHero";
import { useMedia } from "../hooks/useMedia";

const INDUSTRIES = [
  {
    id: "industry-restaurants",
    name: "Restaurants",
    kicker: "Storefront · awnings · sidewalks · dumpster pads",
    body:
      "Grease-laden air film. Rain-tracked sidewalks. The awning nobody remembers to book. We service most restaurant clients before service every fortnight — pre-brunch dawn or post-close overnight. Health-code-adjacent surfaces never wait a full month.",
    services: ["Window Cleaning", "Awning Cleaning", "Sidewalk Cleaning", "Dumpster Pad", "Pest Control"],
  },
  {
    id: "industry-retail",
    name: "Retail",
    kicker: "Flagship storefronts · window installations · sidewalks",
    body:
      "Every smudge on your window is a lost impression from fifteen thousand pedestrians a day. We DI-clean flagship glass overnight so the store opens to a spot-free installation display every morning.",
    services: ["Window Cleaning", "Pressure Washing", "Outdoor Lighting"],
  },
  {
    id: "industry-medical",
    name: "Medical Offices",
    kicker: "Clinical exteriors · quiet-hour service",
    body:
      "Patient perception starts in the parking lot. We service medical properties on weekends and holidays — striping, sealcoat, sidewalks, entry-glass — so no scheduled surgery day is disrupted.",
    services: ["Sidewalk Cleaning", "Window Cleaning", "Sealcoat & Stripe", "Sweeping"],
  },
  {
    id: "industry-office",
    name: "Office Buildings",
    kicker: "Class-A curtain walls · plaza pavers · lobby glass",
    body:
      "First tenant impression is a floor-to-ceiling wall of glass. We DI-clean up to 5 stories from the ground with zero fall exposure, and cycle plaza pavers monthly to keep the entry sequence perfect.",
    services: ["Window Cleaning", "Pressure Washing", "Sidewalk Cleaning"],
  },
  {
    id: "industry-hotels",
    name: "Hotels",
    kicker: "Porte-cochère · valet approach · exterior stone",
    body:
      "Guests judge the property from thirty feet away. We keep porte-cochères, valet driveways, and stone entry walls immaculate on a bi-weekly cadence, with a full DI window rotation quarterly.",
    services: ["Pressure Washing", "Window Cleaning", "Outdoor Lighting"],
  },
  {
    id: "industry-apartments",
    name: "Apartment Communities",
    kicker: "Multi-family exteriors · common area cleanliness",
    body:
      "From luxury pre-war doorman buildings to garden apartments — we cover common-area glass, breezeways, entrances, garbage areas, and seasonal snow. Residents notice on day one.",
    services: ["Window Cleaning", "Pressure Washing", "Snow Removal", "Dumpster Pad"],
  },
  {
    id: "industry-property-mgr",
    name: "Property Managers",
    kicker: "Portfolio-wide coverage · one COI · one invoice",
    body:
      "Property managers running twenty-plus buildings are our largest client segment. One certificate of insurance, one point of contact, one monthly invoice — servicing an entire portfolio to a single standard.",
    services: ["All Services", "Portfolio Retainer"],
  },
  {
    id: "industry-government",
    name: "Government Buildings",
    kicker: "Civic pride · prevailing wage compliant",
    body:
      "Municipal contracts require prevailing wage documentation, DBE compliance, and a documentation trail. We come pre-compliant, with public-work-experienced foremen and a legal team that speaks agency.",
    services: ["Pressure Washing", "Sidewalk Cleaning", "Window Cleaning"],
  },
  {
    id: "industry-schools",
    name: "Schools",
    kicker: "Summer resets · weekend service · child-safe cleaning",
    body:
      "Schools reset over summers and long weekends. We move fast — pressure washing entire campuses, DI-cleaning all classroom-facing glass, and re-striping student pickup lanes in a single 72-hour window.",
    services: ["Pressure Washing", "Window Cleaning", "Sealcoat & Stripe"],
  },
  {
    id: "industry-churches",
    name: "Churches & Parishes",
    kicker: "Historic masonry · sensitive cleaning",
    body:
      "Landmark parishes need soft-wash technique — 500 PSI, warm water, no chemical residue on stone. We treat aged limestone, sandstone, and terracotta with the same care as museum restoration crews.",
    services: ["Soft Washing", "Window Cleaning", "Graffiti Removal"],
  },
  {
    id: "industry-shopping-centers",
    name: "Shopping Centers",
    kicker: "Portfolio-scale parking · tenant-facing storefronts",
    body:
      "Sealcoat once, stripe once, then a monthly sweep and quarterly pressure wash of the sidewalks and glass. We coordinate tenant notifications so no anchor store is disrupted.",
    services: ["Sealcoat & Stripe", "Sweeping", "Pressure Washing"],
  },
  {
    id: "industry-industrial",
    name: "Industrial Facilities",
    kicker: "Heavy-duty exterior · loading docks · yard sweeping",
    body:
      "Industrial exteriors deal with real grime — hydraulic fluid, brake dust, delivery pallets. We use 4000 PSI hot-water rigs and industrial regenerative-air sweepers to reset yards to inspection-ready daily.",
    services: ["Pressure Washing", "Sweeping", "Sealcoat"],
  },
  {
    id: "industry-warehouses",
    name: "Warehouses & Logistics",
    kicker: "Truck bays · fleet apron · perimeter",
    body:
      "Fleet aprons pool oil. Loading dock lines vanish under boot traffic. We cycle warehouse exteriors on a quarterly rotation — sealcoat, stripe, sweep, and pressure-wash the truck bay concrete.",
    services: ["Sealcoat & Stripe", "Pressure Washing", "Sweeping"],
  },
  {
    id: "industry-dealerships",
    name: "Car Dealerships",
    kicker: "Showroom glass · inventory lot · display island",
    body:
      "The showroom is a glass box. The forecourt is where deals happen. We DI-clean showroom glass weekly, sealcoat the display island annually, and stripe with mirror-crisp lines that photograph well on inventory listings.",
    services: ["Window Cleaning", "Sealcoat & Stripe", "Pressure Washing"],
  },
];

export default function Industries() {
  const { url } = useMedia();
  return (
    <div className="min-h-screen" data-testid="industries-page">
      <PageHero
        eyebrow="Industries We Serve"
        title={<>Fourteen verticals. <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>One standard.</span></>}
        subtitle="From luxury restaurants to Class-A office towers to school campuses — each industry has its own cadence, compliance, and quiet-hour rules. We know all of them."
        image={url("industry-office") || url("outdoor-lighting")}
        kicker="Find your fit below."
      />

      <section className="py-24 md:py-32" style={{ background: "var(--bg)" }}>
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {INDUSTRIES.map((ind, i) => (
              <motion.article
                key={ind.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="glass-card overflow-hidden group cursor-pointer flex flex-col"
                data-testid={`industry-card-${ind.id}`}
                data-cursor="hover"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  {url(ind.id) ? (
                    <img src={url(ind.id)} alt={ind.name} className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" draggable={false} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-mono text-[10px] uppercase tracking-[0.36em]" style={{ background: "linear-gradient(135deg, rgba(198,168,124,0.08), rgba(0,0,0,0.6))", color: "var(--text-3)" }}>
                      {ind.name}
                    </div>
                  )}
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.85) 100%)" }} />
                  <div className="absolute bottom-5 left-6 right-6 flex items-end justify-between">
                    <div>
                      <div className="font-display text-2xl md:text-3xl" style={{ letterSpacing: "-0.02em" }}>{ind.name}</div>
                      <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.4em]" style={{ color: "var(--gold)" }}>{ind.kicker}</div>
                    </div>
                  </div>
                </div>
                <div className="p-7 flex-1 flex flex-col">
                  <p className="font-body" style={{ color: "var(--text-2)", lineHeight: 1.7, fontSize: 15 }}>{ind.body}</p>
                  <div className="mt-6 pt-5 flex flex-wrap gap-2" style={{ borderTop: "1px solid var(--line)" }}>
                    {ind.services.map((s) => (
                      <span key={s} className="pill" style={{ fontSize: 10 }}>{s}</span>
                    ))}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-28 md:py-36 text-center" style={{ background: "var(--bg-2)" }}>
        <h2 className="font-display text-5xl md:text-7xl leading-[0.95] max-w-4xl mx-auto px-6" style={{ letterSpacing: "-0.035em" }}>
          Your vertical, our <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>protocol.</span>
        </h2>
        <p className="mt-8 max-w-2xl mx-auto px-6 font-body text-lg" style={{ color: "var(--text-2)", lineHeight: 1.7 }}>
          Every industry has its own cadence and compliance rules. Book a walk-through and we'll build a service calendar around yours.
        </p>
        <div className="mt-12 flex flex-wrap justify-center gap-5">
          <Link to="/pricing" className="btn-glow" data-testid="industries-cta-plans" data-cursor="hover">
            See Instant Plans <ArrowUpRight size={13} />
          </Link>
        </div>
      </section>
    </div>
  );
}
